import { ExternalLink, LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { DocumentDetailsVersion } from "~/api/types";
import { getOfficeURIScheme } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { DocumentVersionStatus } from "~/config/types";

type ValidationError = "missing-published-version" | "invalid-file-type";

interface OfficeUriResult {
  versionId: number;
  uri: string | null;
}

interface OpenInWordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: DocumentDetailsVersion | null;
}

const launchWord = (uri: string) => {
  window.location.href = uri;
};

export function OpenInWordDialog({
  open,
  onOpenChange,
  version,
}: OpenInWordDialogProps) {
  const { t } = useTranslation();
  const [officeUriResult, setOfficeUriResult] =
    useState<OfficeUriResult | null>(null);

  const versionId = version?.id;
  const validationError: ValidationError | null =
    version?.status !== DocumentVersionStatus.published ||
    version.publishedAt === null
      ? "missing-published-version"
      : version.fileType !== "docx"
        ? "invalid-file-type"
        : null;

  useEffect(() => {
    if (!open || validationError || versionId === undefined) return;

    let cancelled = false;

    void getOfficeURIScheme(versionId)
      .then(({ uri }) => {
        if (cancelled) return;

        setOfficeUriResult({ versionId, uri });
        if (uri) launchWord(uri);
      })
      .catch(() => {
        if (!cancelled) {
          setOfficeUriResult({ versionId, uri: null });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, validationError, versionId]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setOfficeUriResult(null);
    }
    onOpenChange(nextOpen);
  };

  const isLoading =
    validationError === null &&
    (officeUriResult === null || officeUriResult.versionId !== versionId);
  const officeUri =
    officeUriResult?.versionId === versionId
      ? (officeUriResult?.uri ?? null)
      : null;
  const requestFailed =
    validationError === null && !isLoading && officeUri === null;
  const isError = validationError !== null || requestFailed;
  const description = validationError
    ? validationError === "missing-published-version"
      ? t(($) => $.commons.workflow.noPublishedVersion)
      : t(($) => $.commons.workflow.wordFileRequired)
    : requestFailed
      ? t(($) => $.commons.workflow.openInWordFailed)
      : t(($) => $.commons.workflow.openingInWordDescription);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isLoading && <LoaderCircle className="size-4 animate-spin" />}
            {isError
              ? t(($) => $.commons.workflow.openInWordFailed)
              : t(($) => $.commons.workflow.openingInWord)}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t(($) => $.commons.buttons.close)}
            </Button>
          </DialogClose>
          {officeUri && (
            <Button type="button" onClick={() => launchWord(officeUri)}>
              <ExternalLink data-icon="inline-start" />
              {t(($) => $.commons.workflow.openInOffice, {
                officeType: "Word",
              })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
